import { alpha, createTheme, lighten, darken } from '@mui/material';
import '@mui/lab/themeAugmentation';

// PROFESSIONAL GREEN COLOR PALETTE 🌿
const themeColors = {
  // Green-based primary colors
  primary: '#4CAF50',        // Material Green - Professional & Fresh
  secondary: '#00897B',      // Teal - Complements green beautifully
  success: '#66BB6A',        // Light Green
  warning: '#FFA952',        // Orange/Amber
  error: '#F44336',          // Material Red
  info: '#2196F3',           // Material Blue
  black: '#1A1A2E',          // Deep Navy
  white: '#FFFFFF',
  primaryAlt: '#43A047',     // Green variant
  accent1: '#81C784',        // Light Green
  accent2: '#A5D6A7',        // Pale Green
  accent3: '#00897B',        // Teal
  teal: '#00897B',           // Teal
  purple: '#9C27B0',         // Material Purple
  neonBlue: '#2196F3',       // Blue
  neonGreen: '#4CAF50'       // Primary Green
};

const colors = {
  gradients: {
    // PROFESSIONAL GRADIENT COMBINATIONS ✨
    primary: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
    secondary: 'linear-gradient(135deg, #00897B 0%, #26A69A 100%)',

    // Modern gradients
    neon1: 'linear-gradient(135deg, #26A69A 0%, #00897B 100%)',
    neon2: 'linear-gradient(135deg, #2196F3 0%, #42A5F5 100%)',
    neon3: 'linear-gradient(135deg, #FFA952 0%, #FFC371 100%)',

    // Professional combinations
    sunset: 'linear-gradient(135deg, #4CAF50 0%, #FFA952 50%, #2196F3 100%)',
    ocean: 'linear-gradient(135deg, #2196F3 0%, #00897B 100%)',
    fire: 'linear-gradient(135deg, #F44336 0%, #FF5722 50%, #FFA952 100%)',
    cosmic: 'linear-gradient(135deg, #00897B 0%, #2196F3 50%, #4CAF50 100%)',
    aurora: 'linear-gradient(135deg, #26A69A 0%, #2196F3 50%, #4CAF50 100%)',

    // Mesh gradients - ultra modern
    mesh1: 'linear-gradient(135deg, #4CAF50 0%, #2196F3 25%, #00897B 50%, #66BB6A 75%, #4CAF50 100%)',
    mesh2: 'radial-gradient(circle at 20% 50%, #4CAF50 0%, transparent 50%), radial-gradient(circle at 80% 80%, #2196F3 0%, transparent 50%), radial-gradient(circle at 40% 80%, #00897B 0%, transparent 50%), linear-gradient(135deg, #26A69A 0%, #4CAF50 100%)',

    // Glassmorphism backgrounds
    glass1: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(0, 137, 123, 0.1) 100%)',
    glass2: 'linear-gradient(135deg, rgba(0, 137, 123, 0.1) 0%, rgba(33, 150, 243, 0.1) 100%)',

    // Button gradients - Professional
    button1: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
    button2: 'linear-gradient(135deg, #00897B 0%, #00695C 100%)',
    button3: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',

    // Dark gradients
    dark1: 'linear-gradient(135deg, #1A1A2E 0%, #2D2D44 100%)',
    dark2: 'linear-gradient(135deg, #16213E 0%, #1A1A2E 100%)',

    // Success, warning, error gradients
    successGradient: 'linear-gradient(135deg, #66BB6A 0%, #81C784 100%)',
    warningGradient: 'linear-gradient(135deg, #FFA952 0%, #FFC371 100%)',
    errorGradient: 'linear-gradient(135deg, #F44336 0%, #EF5350 100%)',
    infoGradient: 'linear-gradient(135deg, #2196F3 0%, #42A5F5 100%)',
  },
  shadows: {
    // Enhanced colored shadows for professional effect
    success: '0 10px 40px 0 rgba(76, 175, 80, 0.4)',
    error: '0 10px 40px 0 rgba(244, 67, 54, 0.4)',
    info: '0 10px 40px 0 rgba(33, 150, 243, 0.4)',
    primary: '0 10px 40px 0 rgba(76, 175, 80, 0.5)',
    warning: '0 10px 40px 0 rgba(255, 169, 82, 0.4)',

    // Modern shadow system
    soft: '0 2px 8px rgba(0, 0, 0, 0.08)',
    medium: '0 8px 24px rgba(0, 0, 0, 0.12)',
    strong: '0 16px 48px rgba(0, 0, 0, 0.16)',
    intense: '0 24px 64px rgba(0, 0, 0, 0.2)',

    // Glow effects
    neonGreen: '0 0 20px rgba(76, 175, 80, 0.6), 0 0 40px rgba(76, 175, 80, 0.4)',
    neonTeal: '0 0 20px rgba(0, 137, 123, 0.6), 0 0 40px rgba(0, 137, 123, 0.4)',
    neonBlue: '0 0 20px rgba(33, 150, 243, 0.6), 0 0 40px rgba(33, 150, 243, 0.4)',

    // Glassmorphism shadow
    glass: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  },
  layout: {
    general: {
      // Subtle gradient background
      bodyBg: 'linear-gradient(135deg, #F5F7FA 0%, #E8EAF6 100%)'
    },
    sidebar: {
      // Dark gradient with subtle color
      background: 'linear-gradient(180deg, #1A1A2E 0%, #16213E 100%)',
      textColor: '#FFFFFF',
      dividerBg: 'rgba(255, 255, 255, 0.08)',
      menuItemColor: 'rgba(255, 255, 255, 0.8)',
      menuItemColorActive: '#FFFFFF',
      menuItemBg: 'transparent',
      menuItemBgActive: 'linear-gradient(90deg, rgba(76, 175, 80, 0.2) 0%, rgba(0, 137, 123, 0.2) 100%)',
      menuItemIconColor: '#4CAF50',
      menuItemIconColorActive: '#66BB6A',
      menuItemHeadingColor: '#00897B'
    }
  },
  alpha: {
    white: {
      5: alpha(themeColors.white, 0.05),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    trueWhite: {
      5: alpha(themeColors.white, 0.05),
      10: alpha(themeColors.white, 0.1),
      30: alpha(themeColors.white, 0.3),
      50: alpha(themeColors.white, 0.5),
      70: alpha(themeColors.white, 0.7),
      100: themeColors.white
    },
    black: {
      5: alpha(themeColors.black, 0.05),
      10: alpha(themeColors.black, 0.1),
      30: alpha(themeColors.black, 0.3),
      50: alpha(themeColors.black, 0.5),
      70: alpha(themeColors.black, 0.7),
      100: themeColors.black
    }
  },
  secondary: {
    lighter: lighten(themeColors.secondary, 0.85),
    light: lighten(themeColors.secondary, 0.25),
    main: themeColors.secondary,
    dark: darken(themeColors.secondary, 0.2)
  },
  primary: {
    lighter: lighten(themeColors.primary, 0.85),
    light: lighten(themeColors.primary, 0.3),
    main: themeColors.primary,
    dark: darken(themeColors.primary, 0.2)
  },
  success: {
    lighter: lighten(themeColors.success, 0.85),
    light: lighten(themeColors.success, 0.3),
    main: themeColors.success,
    dark: darken(themeColors.success, 0.2)
  },
  warning: {
    lighter: lighten(themeColors.warning, 0.85),
    light: lighten(themeColors.warning, 0.3),
    main: themeColors.warning,
    dark: darken(themeColors.warning, 0.2)
  },
  error: {
    lighter: lighten(themeColors.error, 0.85),
    light: lighten(themeColors.error, 0.3),
    main: themeColors.error,
    dark: darken(themeColors.error, 0.2)
  },
  info: {
    lighter: lighten(themeColors.info, 0.85),
    light: lighten(themeColors.info, 0.3),
    main: themeColors.info,
    dark: darken(themeColors.info, 0.2)
  }
};

export const PureLightTheme = createTheme({
  colors: {
    gradients: {
      blue1: colors.gradients.neon2,
      blue2: colors.gradients.button3,
      blue3: colors.gradients.ocean,
      blue4: colors.gradients.infoGradient,
      blue5: colors.gradients.mesh1,
      orange1: colors.gradients.fire,
      orange2: colors.gradients.sunset,
      orange3: colors.gradients.warningGradient,
      purple1: colors.gradients.cosmic,
      purple3: colors.gradients.neon2,
      pink1: colors.gradients.primary,
      pink2: colors.gradients.primary,
      green1: colors.gradients.successGradient,
      green2: colors.gradients.neon1,
      black1: colors.gradients.dark1,
      black2: colors.gradients.dark2
    },
    shadows: {
      success: colors.shadows.success,
      error: colors.shadows.error,
      primary: colors.shadows.primary,
      info: colors.shadows.info,
      warning: colors.shadows.warning
    },
    alpha: {
      white: colors.alpha.white,
      trueWhite: colors.alpha.trueWhite,
      black: colors.alpha.black
    },
    secondary: {
      lighter: alpha(themeColors.secondary, 0.1),
      light: lighten(themeColors.secondary, 0.3),
      main: themeColors.secondary,
      dark: darken(themeColors.secondary, 0.2)
    },
    primary: {
      lighter: alpha(themeColors.primary, 0.1),
      light: lighten(themeColors.primary, 0.3),
      main: themeColors.primary,
      dark: darken(themeColors.primary, 0.2)
    },
    success: {
      lighter: alpha(themeColors.success, 0.1),
      light: lighten(themeColors.success, 0.3),
      main: themeColors.success,
      dark: darken(themeColors.success, 0.2)
    },
    warning: {
      lighter: alpha(themeColors.warning, 0.1),
      light: lighten(themeColors.warning, 0.3),
      main: themeColors.warning,
      dark: darken(themeColors.warning, 0.2)
    },
    error: {
      lighter: alpha(themeColors.error, 0.1),
      light: lighten(themeColors.error, 0.3),
      main: themeColors.error,
      dark: darken(themeColors.error, 0.2)
    },
    info: {
      lighter: alpha(themeColors.info, 0.1),
      light: lighten(themeColors.info, 0.3),
      main: themeColors.info,
      dark: darken(themeColors.info, 0.2)
    }
  },
  general: {
    reactFrameworkColor: '#00D8FF',
    borderRadiusSm: '12px',
    borderRadius: '16px',
    borderRadiusLg: '20px',
    borderRadiusXl: '28px'
  },
  sidebar: {
    background: colors.layout.sidebar.background,
    textColor: colors.layout.sidebar.textColor,
    dividerBg: colors.layout.sidebar.dividerBg,
    menuItemColor: colors.layout.sidebar.menuItemColor,
    menuItemColorActive: colors.layout.sidebar.menuItemColorActive,
    menuItemBg: colors.layout.sidebar.menuItemBg,
    menuItemBgActive: colors.layout.sidebar.menuItemBgActive,
    menuItemIconColor: colors.layout.sidebar.menuItemIconColor,
    menuItemIconColorActive: colors.layout.sidebar.menuItemIconColorActive,
    menuItemHeadingColor: colors.layout.sidebar.menuItemHeadingColor,
    boxShadow: '8px 0 40px rgba(0, 0, 0, 0.15)',
    width: '280px',
    collapsedWidth: '80px'
  },
  header: {
    height: '72px',
    // Glassmorphism header
    background: 'rgba(255, 255, 255, 0.75)',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    textColor: colors.alpha.black[100]
  },
  spacing: 9,
  palette: {
    common: {
      black: colors.alpha.black[100],
      white: colors.alpha.white[100]
    },
    mode: 'light',
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark,
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: colors.secondary.dark,
      contrastText: '#FFFFFF'
    },
    error: {
      light: colors.error.light,
      main: colors.error.main,
      dark: colors.error.dark,
      contrastText: '#FFFFFF'
    },
    success: {
      light: colors.success.light,
      main: colors.success.main,
      dark: colors.success.dark,
      contrastText: '#FFFFFF'
    },
    info: {
      light: colors.info.light,
      main: colors.info.main,
      dark: colors.info.dark,
      contrastText: '#FFFFFF'
    },
    warning: {
      light: colors.warning.light,
      main: colors.warning.main,
      dark: colors.warning.dark,
      contrastText: '#FFFFFF'
    },
    text: {
      primary: colors.alpha.black[100],
      secondary: colors.alpha.black[70],
      disabled: colors.alpha.black[50]
    },
    background: {
      paper: '#FFFFFF',
      default: '#F5F7FA'
    },
    action: {
      active: colors.alpha.black[100],
      hover: alpha(themeColors.primary, 0.08),
      hoverOpacity: 0.08,
      selected: alpha(themeColors.primary, 0.12),
      selectedOpacity: 0.12,
      disabled: colors.alpha.black[50],
      disabledBackground: colors.alpha.black[5],
      disabledOpacity: 0.38,
      focus: alpha(themeColors.primary, 0.12),
      focusOpacity: 0.12,
      activatedOpacity: 0.16
    },
    tonalOffset: 0.5
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1840
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@import': 'url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Poppins:wght@400;500;600;700;800;900&display=swap")',
        'html, body': {
          width: '100%',
          height: '100%',
          background: colors.layout.general.bodyBg
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          flex: 1
        },
        '#root': {
          width: '100%',
          height: '100%',
          display: 'flex',
          flex: 1,
          flexDirection: 'column'
        },
        html: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased'
        },
        // DRAMATIC ANIMATIONS 🎭
        '@keyframes float': {
          '0%, 100%': {
            transform: 'translateY(0px)'
          },
          '50%': {
            transform: 'translateY(-15px)'
          }
        },
        '@keyframes pulse': {
          '0%, 100%': {
            transform: 'scale(1)',
            opacity: 1
          },
          '50%': {
            transform: 'scale(1.05)',
            opacity: 0.9
          }
        },
        '@keyframes shimmer': {
          '0%': {
            backgroundPosition: '-1000px 0'
          },
          '100%': {
            backgroundPosition: '1000px 0'
          }
        },
        '@keyframes slideInFromLeft': {
          '0%': {
            opacity: 0,
            transform: 'translateX(-50px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateX(0)'
          }
        },
        '@keyframes slideInFromRight': {
          '0%': {
            opacity: 0,
            transform: 'translateX(50px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateX(0)'
          }
        },
        '@keyframes zoomIn': {
          '0%': {
            opacity: 0,
            transform: 'scale(0.85)'
          },
          '100%': {
            opacity: 1,
            transform: 'scale(1)'
          }
        },
        '@keyframes rotateIn': {
          '0%': {
            opacity: 0,
            transform: 'rotate(-10deg) scale(0.9)'
          },
          '100%': {
            opacity: 1,
            transform: 'rotate(0deg) scale(1)'
          }
        },
        '@keyframes neonGlow': {
          '0%, 100%': {
            boxShadow: `0 0 10px ${themeColors.primary}, 0 0 20px ${themeColors.primary}, 0 0 30px ${themeColors.primary}`
          },
          '50%': {
            boxShadow: `0 0 20px ${themeColors.primary}, 0 0 30px ${themeColors.primary}, 0 0 40px ${themeColors.primary}, 0 0 50px ${themeColors.primary}`
          }
        },
        '@keyframes gradientShift': {
          '0%': {
            backgroundPosition: '0% 50%'
          },
          '50%': {
            backgroundPosition: '100% 50%'
          },
          '100%': {
            backgroundPosition: '0% 50%'
          }
        },
        '@keyframes bounce': {
          '0%, 100%': {
            transform: 'translateY(0)'
          },
          '50%': {
            transform: 'translateY(-10px)'
          }
        },
        '@keyframes rippleEffect': {
          '0%': {
            transform: 'scale(0)',
            opacity: 1
          },
          '100%': {
            transform: 'scale(4)',
            opacity: 0
          }
        }
      }
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(themeColors.black, 0.6),
          backdropFilter: 'blur(12px)',
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'blur(6px)'
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: false
      },
      styleOverrides: {
        root: {
          fontWeight: 700,
          textTransform: 'none',
          paddingLeft: 32,
          paddingRight: 32,
          borderRadius: 16,
          fontSize: '1rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255, 255, 255, 0.2)',
            transform: 'translateX(-100%)',
            transition: 'transform 0.3s ease'
          },
          '&:hover': {
            transform: 'translateY(-4px) scale(1.02)',
            boxShadow: colors.shadows.intense,
            '&::before': {
              transform: 'translateX(0)'
            }
          },
          '&:active': {
            transform: 'translateY(-2px) scale(0.98)'
          }
        },
        contained: {
          boxShadow: colors.shadows.medium
        },
        containedPrimary: {
          background: colors.gradients.button1,
          backgroundSize: '200% 200%',
          animation: 'gradientShift 3s ease infinite',
          color: '#FFFFFF',
          '&:hover': {
            background: colors.gradients.sunset,
            boxShadow: colors.shadows.neonGreen
          }
        },
        containedSecondary: {
          background: colors.gradients.button3,
          backgroundSize: '200% 200%',
          animation: 'gradientShift 3s ease infinite',
          color: '#FFFFFF',
          '&:hover': {
            boxShadow: colors.shadows.neonBlue
          }
        },
        outlined: {
          borderWidth: 2,
          borderColor: themeColors.primary,
          color: themeColors.primary,
          '&:hover': {
            borderWidth: 2,
            background: alpha(themeColors.primary, 0.08),
            transform: 'translateY(-4px)',
            boxShadow: colors.shadows.medium
          }
        },
        sizeSmall: {
          padding: '10px 24px',
          fontSize: '0.875rem',
          borderRadius: 12
        },
        sizeMedium: {
          padding: '12px 32px',
          fontSize: '1rem',
          borderRadius: 16
        },
        sizeLarge: {
          padding: '16px 40px',
          fontSize: '1.125rem',
          borderRadius: 20
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          padding: 12,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            borderRadius: 14,
            opacity: 0,
            transition: 'opacity 0.3s ease',
            background: colors.gradients.primary
          },
          '&:hover': {
            background: alpha(themeColors.primary, 0.12),
            transform: 'scale(1.15) rotate(5deg)',
            boxShadow: colors.shadows.medium,
            '&::after': {
              opacity: 0.1
            }
          },
          '&:active': {
            transform: 'scale(1.05)'
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: colors.shadows.medium,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: colors.gradients.primary,
            opacity: 0,
            transition: 'opacity 0.3s ease'
          },
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: colors.shadows.intense,
            '&::before': {
              opacity: 1
            }
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 16
        },
        elevation1: {
          boxShadow: colors.shadows.soft
        },
        elevation2: {
          boxShadow: colors.shadows.medium
        },
        elevation3: {
          boxShadow: colors.shadows.strong
        },
        elevation4: {
          boxShadow: colors.shadows.intense
        },
        rounded: {
          borderRadius: 20
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 24,
          boxShadow: colors.shadows.intense,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: '#FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: colors.shadows.soft,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: themeColors.primary,
              borderWidth: 2
            }
          },
          '&.Mui-focused': {
            transform: 'translateY(-2px)',
            boxShadow: colors.shadows.primary,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: themeColors.primary,
              borderWidth: 3
            }
          }
        },
        notchedOutline: {
          borderColor: alpha(themeColors.primary, 0.2),
          borderWidth: 2,
          transition: 'all 0.3s ease'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          fontSize: '0.875rem',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-3px) scale(1.05)',
            boxShadow: colors.shadows.medium
          }
        },
        colorPrimary: {
          background: colors.gradients.primary,
          color: '#FFFFFF'
        },
        colorSecondary: {
          background: colors.gradients.secondary,
          color: '#FFFFFF'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 60,
          height: 36,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 4,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(24px)',
              '& + .MuiSwitch-track': {
                background: colors.gradients.successGradient,
                opacity: 1,
                border: 0,
                boxShadow: colors.shadows.neonGreen
              },
              '& .MuiSwitch-thumb': {
                background: '#FFFFFF',
                boxShadow: colors.shadows.neonGreen
              }
            }
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 28,
            height: 28,
            boxShadow: colors.shadows.medium,
            background: '#FFFFFF'
          },
          '& .MuiSwitch-track': {
            borderRadius: 18,
            backgroundColor: alpha(themeColors.black, 0.2),
            opacity: 1,
            transition: 'all 300ms'
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          padding: '16px 20px',
          fontWeight: 500,
          boxShadow: colors.shadows.soft
        },
        standardSuccess: {
          background: `linear-gradient(135deg, ${alpha(themeColors.success, 0.15)} 0%, ${alpha(themeColors.success, 0.05)} 100%)`,
          color: themeColors.success,
          borderLeft: `4px solid ${themeColors.success}`
        },
        standardError: {
          background: `linear-gradient(135deg, ${alpha(themeColors.error, 0.15)} 0%, ${alpha(themeColors.error, 0.05)} 100%)`,
          color: themeColors.error,
          borderLeft: `4px solid ${themeColors.error}`
        },
        standardWarning: {
          background: `linear-gradient(135deg, ${alpha(themeColors.warning, 0.15)} 0%, ${alpha(themeColors.warning, 0.05)} 100%)`,
          color: themeColors.warning,
          borderLeft: `4px solid ${themeColors.warning}`
        },
        standardInfo: {
          background: `linear-gradient(135deg, ${alpha(themeColors.info, 0.15)} 0%, ${alpha(themeColors.info, 0.05)} 100%)`,
          color: themeColors.info,
          borderLeft: `4px solid ${themeColors.info}`
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: alpha(themeColors.black, 0.95),
          backdropFilter: 'blur(10px)',
          padding: '12px 20px',
          fontSize: 14,
          fontWeight: 600,
          borderRadius: 12,
          boxShadow: colors.shadows.strong
        },
        arrow: {
          color: alpha(themeColors.black, 0.95)
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 10,
          borderRadius: 10,
          backgroundColor: alpha(themeColors.primary, 0.1)
        },
        bar: {
          borderRadius: 10,
          background: colors.gradients.sunset
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '1rem'
        },
        colorDefault: {
          background: colors.gradients.primary,
          color: '#FFFFFF'
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 56
        },
        indicator: {
          height: 4,
          borderRadius: '4px 4px 0 0',
          background: colors.gradients.sunset,
          boxShadow: colors.shadows.primary
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          minHeight: 56,
          transition: 'all 0.3s ease',
          '&:hover': {
            color: themeColors.primary,
            transform: 'translateY(-2px)'
          },
          '&.Mui-selected': {
            color: themeColors.primary,
            fontWeight: 700
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          background: colors.gradients.primary,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 900
        },
        h2: {
          background: colors.gradients.ocean,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 800
        }
      }
    }
  },
  shape: {
    borderRadius: 16
  },
  typography: {
    fontFamily: '"Inter", "Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    h1: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 900,
      fontSize: 48,
      lineHeight: 1.2,
      letterSpacing: '-0.03em'
    },
    h2: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 800,
      fontSize: 40,
      lineHeight: 1.2,
      letterSpacing: '-0.02em'
    },
    h3: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 700,
      fontSize: 32,
      lineHeight: 1.3,
      letterSpacing: '-0.01em'
    },
    h4: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 700,
      fontSize: 26,
      lineHeight: 1.4
    },
    h5: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 700,
      fontSize: 20,
      lineHeight: 1.5
    },
    h6: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
      fontSize: 18,
      lineHeight: 1.5
    },
    body1: {
      fontSize: 16,
      lineHeight: 1.7,
      fontWeight: 400
    },
    body2: {
      fontSize: 14,
      lineHeight: 1.6,
      fontWeight: 400
    },
    button: {
      fontWeight: 700,
      fontSize: 16,
      textTransform: 'none',
      letterSpacing: '0.02em'
    },
    subtitle1: {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.6
    },
    subtitle2: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.6
    }
  },
  shadows: [
    'none',
    colors.shadows.soft,
    colors.shadows.medium,
    colors.shadows.strong,
    colors.shadows.intense,
    '0 12px 24px -10px rgba(0, 0, 0, 0.15)',
    '0 16px 32px -12px rgba(0, 0, 0, 0.18)',
    '0 20px 40px -14px rgba(0, 0, 0, 0.2)',
    colors.shadows.glass,
    '0 28px 56px -18px rgba(0, 0, 0, 0.25)',
    '0 32px 64px -20px rgba(0, 0, 0, 0.28)',
    '0 36px 72px -22px rgba(0, 0, 0, 0.3)',
    '0 40px 80px -24px rgba(0, 0, 0, 0.32)',
    '0 44px 88px -26px rgba(0, 0, 0, 0.34)',
    '0 48px 96px -28px rgba(0, 0, 0, 0.36)',
    '0 52px 104px -30px rgba(0, 0, 0, 0.38)',
    '0 56px 112px -32px rgba(0, 0, 0, 0.4)',
    '0 60px 120px -34px rgba(0, 0, 0, 0.42)',
    '0 64px 128px -36px rgba(0, 0, 0, 0.44)',
    '0 68px 136px -38px rgba(0, 0, 0, 0.46)',
    '0 72px 144px -40px rgba(0, 0, 0, 0.48)',
    '0 76px 152px -42px rgba(0, 0, 0, 0.5)',
    '0 80px 160px -44px rgba(0, 0, 0, 0.52)',
    '0 84px 168px -46px rgba(0, 0, 0, 0.54)',
    '0 88px 176px -48px rgba(0, 0, 0, 0.56)'
  ]
});
